import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Alertaio
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("features")}
              className="text-sm font-medium transition-colors text-slate-600 hover:text-indigo-600 cursor-pointer"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection("how-it-works")}
              className="text-sm font-medium transition-colors text-slate-600 hover:text-indigo-600 cursor-pointer"
            >
              How It Works
            </button>
            
            {/* Tools Dropdown */}
            <div className="relative group">
              <button className="text-sm font-medium transition-colors text-slate-600 hover:text-indigo-600 cursor-pointer flex items-center gap-1">
                Tools
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <Link
                  to="/cost-calculator"
                  className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors first:rounded-t-lg"
                >
                  💰 Cost Calculator
                </Link>
                <Link
                  to="/token-calculator"
                  className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                >
                  🔢 Token Calculator
                </Link>
                <Link
                  to="/provider-selector"
                  className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors last:rounded-b-lg"
                >
                  🎯 Provider Selector
                </Link>
              </div>
            </div>
            
            <Link
              to="/blog"
              className={`text-sm font-medium transition-colors ${
                location.pathname.startsWith("/blog")
                  ? "text-indigo-600"
                  : "text-slate-600 hover:text-indigo-600"
              }`}
            >
              Blog
            </Link>
            <Link
              to="/faq"
              className={`text-sm font-medium transition-colors ${
                isActive("/faq")
                  ? "text-indigo-600"
                  : "text-slate-600 hover:text-indigo-600"
              }`}
            >
              FAQ
            </Link>
          </div>

          {/* CTA Button */}

          <div className="flex items-center gap-4">
            <button
              onClick={() => scrollToSection("bottom-cta")}
              className="hidden sm:inline-block bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all transform hover:scale-105 shadow-md cursor-pointer"
            >
              Get Started Free
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-600 hover:text-indigo-600"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => {
                  scrollToSection("features");
                  setMobileMenuOpen(false);
                }}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 text-left px-4 py-2"
              >
                Features
              </button>
              <button
                onClick={() => {
                  scrollToSection("how-it-works");
                  setMobileMenuOpen(false);
                }}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 text-left px-4 py-2"
              >
                How It Works
              </button>
              <Link
                to="/cost-calculator"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 px-4 py-2"
              >
                💰 Cost Calculator
              </Link>
              <Link
                to="/token-calculator"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 px-4 py-2"
              >
                🔢 Token Calculator
              </Link>
              <Link
                to="/provider-selector"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 px-4 py-2"
              >
                🎯 Provider Selector
              </Link>
              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 px-4 py-2"
              >
                Blog
              </Link>
              <Link
                to="/faq"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 px-4 py-2"
              >
                FAQ
              </Link>
              <button
                onClick={() => {
                  scrollToSection("bottom-cta");
                  setMobileMenuOpen(false);
                }}
                className="text-sm font-medium text-slate-600 hover:text-indigo-600 text-left px-4 py-2"
              >
                Get Started Free
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
